import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import EmailIcon from "@mui/icons-material/Email";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import { useTranslation } from "react-i18next";
import { getTicketById } from "app/services/api/tickets";
import { formatDateTimeSecondsSk } from "app/utils/dateFormat";
import { getTicketStatusLabel } from "app/utils/tickets/getTicketStatusLabel";
import { getTicketValidityDisplay } from "app/utils/tickets/getTicketValidityDisplay";
import { useSnackbar } from "notistack";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import jwtAuthKioskAxios from "app/services/auth/jwtAuthKiosk";
import { setBackendDomain } from "app/services/api/management";

/**
 * Normalize customer workspaces from dsokSettings into a list of { workspaceKey, workspaceName, devices }.
 * devices is array of { deviceKey, name, connectionCode (workspace level), ... }.
 */
function getWorkspacesWithDevices(customer) {
  const raw =
    typeof customer?.dsokSettings === "object" ? customer.dsokSettings : null;
  const parsed =
    raw ??
    (typeof customer?.dsokSettings === "string"
      ? (() => {
          try {
            return JSON.parse(customer.dsokSettings);
          } catch (_) {
            return null;
          }
        })()
      : null);
  const workspaces = parsed?.customer?.workspaces;
  if (!workspaces || typeof workspaces !== "object") return [];
  return Object.keys(workspaces)
    .filter((k) => workspaces[k] != null && typeof workspaces[k] === "object")
    .sort((a, b) => Number(a) - Number(b))
    .map((workspaceKey) => {
      const w = workspaces[workspaceKey];
      const workspaceName = w?.name?.trim() || workspaceKey;
      const devs = w?.devices;
      let deviceList = [];
      if (Array.isArray(devs)) {
        deviceList = devs
          .filter((d) => d != null && typeof d === "object")
          .map((d, i) => ({
            deviceKey: String(i + 1),
            deviceId: d?.deviceId?.trim() || "",
            name: d?.name?.trim() || "",
            connectionCode: w?.connectionCode ?? "",
            localIp: d?.localIp?.trim() || "",
          }));
      } else if (devs && typeof devs === "object") {
        deviceList = Object.keys(devs)
          .filter((k) => devs[k] != null && typeof devs[k] === "object")
          .sort((a, b) => Number(a) - Number(b))
          .map((deviceKey) => {
            const d = devs[deviceKey];
            return {
              deviceKey,
              deviceId: d?.deviceId?.trim() || "",
              name: d?.name?.trim() || "",
              connectionCode: w?.connectionCode ?? "",
              localIp: d?.localIp?.trim() || "",
            };
          });
      }
      return { workspaceKey, workspaceName, devices: deviceList };
    });
}

export const PrintTicketPrinterSelect = ({
  customer,
  customerId,
  ticketCode,
  onPrintSuccess,
  enqueueSnackbar,
}) => {
  const { t } = useTranslation();
  const [loadingTileKey, setLoadingTileKey] = React.useState(null);
  const showSnackbar = enqueueSnackbar ?? (() => {});

  const list = React.useMemo(
    () => getWorkspacesWithDevices(customer),
    [customer],
  );
  const hasAnyPrinter = list.some((item) => item.devices.length > 0);
  const isPending = loadingTileKey != null;

  const handlePrinterClick = React.useCallback(
    async (workspaceKey, workspaceName, device) => {
      const connectionCode = (device.connectionCode ?? "").trim();
      const code = (ticketCode ?? "").trim();
      const cid = customer?.customerId;
      if (!connectionCode || !code) {
        showSnackbar(
          t("pages.dsokDetails.accessSystem.detail.actions.print.error", {
            defaultValue: "Tlač zlyhala",
          }),
          { variant: "error" },
        );
        return;
      }
      if (!cid) {
        showSnackbar(
          t("pages.dsokDetails.accessSystem.detail.actions.print.error", {
            defaultValue: "Tlač zlyhala",
          }),
          { variant: "error" },
        );
        return;
      }
      const tileKey = `${workspaceKey}-${device?.deviceKey}`;
      setLoadingTileKey(tileKey);
      try {
        setBackendDomain(jwtAuthKioskAxios);
        await jwtAuthKioskAxios.post(
          `/api/v1/dla/${encodeURIComponent(connectionCode)}/commands/print-ticket-by-code/${encodeURIComponent(code)}/printerIp/${encodeURIComponent(device?.localIp)}`,
          { customerId: cid },
        );
        showSnackbar(
          t("pages.dsokDetails.accessSystem.detail.actions.print.success", {
            defaultValue: "Dáta pre tlač boli pripravené",
          }),
          { variant: "success" },
        );
        if (typeof onPrintSuccess === "function") {
          onPrintSuccess();
        }
      } catch (err) {
        showSnackbar(
          t("pages.dsokDetails.accessSystem.detail.actions.print.error", {
            defaultValue: "Tlač zlyhala",
          }),
          { variant: "error" },
        );
      } finally {
        setLoadingTileKey(null);
      }
    },
    [customer, customerId, onPrintSuccess, showSnackbar, t, ticketCode],
  );

  if (list.length === 0 || !hasAnyPrinter) {
    return (
      <Alert severity="info" variant="outlined" sx={{ mt: 0 }}>
        {t("pages.dsokDetails.accessSystem.detail.actions.print.noPrinters", {
          defaultValue:
            "Zákazník nemá nastavené pracoviská s tlačiareňami. Najprv pridajte pracoviská a zariadenia v sekcii Pracoviská.",
        })}
      </Alert>
    );
  }

  return (
    <Stack spacing={2} sx={{ pt: 0.5 }}>
      {list.map(({ workspaceKey, workspaceName, devices }) => (
        <Box key={workspaceKey}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            {workspaceName}
          </Typography>
          {devices.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t(
                "pages.dsokDetails.accessSystem.detail.actions.print.noPrintersInWorkspace",
                {
                  defaultValue: "Bez tlačiarní",
                },
              )}
            </Typography>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              {devices.map((device) => {
                const tileKey = `${workspaceKey}-${device.deviceKey}`;
                const isLoading = loadingTileKey === tileKey;
                return (
                  <Button
                    key={device.deviceKey}
                    variant="outlined"
                    disabled={isPending}
                    onClick={() =>
                      handlePrinterClick(workspaceKey, workspaceName, device)
                    }
                    sx={{
                      flexDirection: "column",
                      minWidth: 100,
                      minHeight: 88,
                      py: 1.5,
                      px: 2,
                      textTransform: "none",
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={32} sx={{ mb: 0.5 }} />
                    ) : (
                      <PrintIcon
                        sx={{ fontSize: 36, mb: 0.5, color: "primary.main" }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{ textAlign: "center" }}
                    >
                      {device.name ||
                        `${t("pages.dsokDetails.accessSystem.detail.actions.print.printer", { defaultValue: "Tlačiareň" })} ${device.deviceKey}`}
                    </Typography>
                  </Button>
                );
              })}
            </Box>
          )}
        </Box>
      ))}
    </Stack>
  );
};

const normalizeStatus = (raw) =>
  String(raw || "")
    .trim()
    .toLowerCase();
const normalizeEventType = (raw) =>
  String(raw || "")
    .trim()
    .toLowerCase();

const getStatusChipProps = (status) => {
  switch (status) {
    case "active":
      return { color: "success" };
    case "ready":
      return { color: "info" };
    case "unpaid":
      return {
        color: "warning",
        sx: {
          bgcolor: "#FDD835",
          color: "#000",
        },
      };
    case "expired":
      return { color: "warning" };
    case "cancelled":
    case "canceled":
    case "refunded":
    case "blocked":
      return { color: "error" };
    default:
      return { color: "default" };
  }
};

const getAtValue = (obj) =>
  obj?.at ?? obj?.createdAt ?? obj?.timestamp ?? obj?.time ?? obj?.date ?? null;

const sortByAtAsc = (list) => {
  const arr = Array.isArray(list) ? [...list] : [];
  arr.sort((a, b) => {
    const ta = new Date(getAtValue(a) || 0).getTime();
    const tb = new Date(getAtValue(b) || 0).getTime();
    return ta - tb;
  });
  return arr;
};

const noDash = (v) => (v === undefined || v === null || v === "" ? "—" : v);

const SectionCard = ({ title, children }) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      p: 2,
    }}
  >
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Divider sx={{ mb: 1.5 }} />
    {children}
  </Box>
);

const TicketDetailModal = ({ open, onClose, ticketId, initialTicket }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { showDialog, hideDialog } = useJumboDialog();
  const { getAuthUser } = useJumboAuth();
  const [status, setStatus] = React.useState("idle"); // idle | loading | loaded | error
  const [detail, setDetail] = React.useState(null);
  const [loadError, setLoadError] = React.useState(null);
  const [isReprinting, setIsReprinting] = React.useState(false);

  const cacheRef = React.useRef({ id: null, data: null });
  const authUser = getAuthUser?.() || {};
  const customer = authUser?.customer ?? null;

  const safeParse = React.useCallback((maybeJson) => {
    if (!maybeJson) return null;
    if (typeof maybeJson === "object") return maybeJson;
    if (typeof maybeJson !== "string") return null;
    try {
      return JSON.parse(maybeJson);
    } catch (_e) {
      return null;
    }
  }, []);

  const accessSystemSettings = React.useMemo(() => {
    const dsokSettingsParsed = safeParse(customer?.dsokSettings);
    return (
      dsokSettingsParsed?.settings?.accessSystem ??
      customer?.settings?.accessSystem ??
      null
    );
  }, [customer?.dsokSettings, customer?.settings?.accessSystem, safeParse]);

  const accessSystemEnabled = accessSystemSettings?.enabled === true;
  const canPrintTickets =
    accessSystemEnabled && accessSystemSettings?.printTickets === true;
  const canSendEmail =
    accessSystemEnabled && accessSystemSettings?.sendEmailEnabled === true;
  const canSendSms =
    accessSystemEnabled && accessSystemSettings?.sendSmsEnabled === true;
  const hasActions = canPrintTickets || canSendEmail || canSendSms;

  React.useEffect(() => {
    if (!open) return;
    if (!ticketId) {
      setStatus("error");
      setLoadError({ message: "Missing ticketId" });
      return;
    }

    if (cacheRef.current?.id === String(ticketId) && cacheRef.current?.data) {
      setDetail(cacheRef.current.data);
      setStatus("loaded");
      setLoadError(null);
      return;
    }

    setStatus("loading");
    setLoadError(null);

    getTicketById(ticketId)
      .then((data) => {
        cacheRef.current = { id: String(ticketId), data };
        setDetail(data);
        setStatus("loaded");
      })
      .catch((e) => {
        setLoadError({
          message: e?.response?.data?.message ?? e?.message ?? String(e),
          status: e?.response?.status,
        });
        setStatus("error");
      });
  }, [open, ticketId]);

  const data =
    detail?.ticket ?? detail ?? initialTicket?.ticket ?? initialTicket;

  const ticketCode =
    data?.ticketCode ?? data?.code ?? data?.ticket_code ?? data?.ticket ?? "";
  const statusKey = normalizeStatus(data?.status);
  const chipProps = getStatusChipProps(statusKey);
  const statusLabel = getTicketStatusLabel(t, statusKey);

  const validity = getTicketValidityDisplay(t, data, { withDateTime: true });

  const resolveI18nString = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "object") return value.sk || value.en || null;
    if (value === "[object Object]") return null;
    return value;
  };

  const productName =
    resolveI18nString(data?.productSnapshot?.name) ??
    resolveI18nString(data?.productSnapshot?.title) ??
    resolveI18nString(data?.productSnapshot?.label) ??
    "";
  const productDesc = resolveI18nString(data?.productSnapshot?.description) ?? "";
  const price =
    data?.productSnapshot?.price ??
    data?.productSnapshot?.unitPrice ??
    data?.price ??
    null;
  const currency =
    data?.productSnapshot?.currency ??
    data?.currency ??
    data?.productSnapshot?.priceCurrency ??
    "";

  const events = React.useMemo(
    () => sortByAtAsc(data?.events).reverse(),
    [data?.events],
  );
  const statusHistory = React.useMemo(
    () => sortByAtAsc(data?.statusHistory).reverse(),
    [data?.statusHistory],
  );
  const reprints = React.useMemo(
    () => sortByAtAsc(data?.reprints),
    [data?.reprints],
  );

  const noDataLabel = t("pages.dsokDetails.accessSystem.detail.noData", {
    defaultValue: "No data",
  });
  const noStatusHistoryLabel = t(
    "pages.dsokDetails.accessSystem.detail.noStatusHistory",
    { defaultValue: "Bez histórie zmien stavu" },
  );

  const handlePrint = React.useCallback(() => {
    if (!ticketId || isReprinting) return;

    showDialog({
      variant: "default",
      title: t(
        "pages.dsokDetails.accessSystem.detail.actions.print.selectPrinterTitle",
        {
          defaultValue: "Vyberte tlačiareň",
        },
      ),
      content: (
        <PrintTicketPrinterSelect
          customer={customer}
          customerId={customer?.id ?? customer?._id}
          ticketCode={ticketCode}
          onPrintSuccess={hideDialog}
          enqueueSnackbar={enqueueSnackbar}
        />
      ),
      actions: (
        <Button onClick={hideDialog}>
          {t("buttons.cancel", { defaultValue: "Zrušiť" })}
        </Button>
      ),
      maxWidth: "sm",
      fullWidth: true,
    });
  }, [
    customer,
    enqueueSnackbar,
    hideDialog,
    isReprinting,
    showDialog,
    t,
    ticketCode,
    ticketId,
  ]);

  const handleComingSoon = React.useCallback(() => {
    enqueueSnackbar(
      t("pages.dsokDetails.accessSystem.detail.actions.comingSoon", {
        defaultValue: "Funkcia bude doplnená neskôr.",
      }),
      { variant: "info" },
    );
  }, [enqueueSnackbar, t]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {t("pages.dsokDetails.accessSystem.detail.title", {
          defaultValue: "Detail lístka",
        })}
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          {hasActions ? (
            <>
              {canPrintTickets ? (
                <Tooltip
                  title={t(
                    "pages.dsokDetails.accessSystem.detail.actions.print.tooltip",
                    { defaultValue: "Tlač" },
                  )}
                >
                  <span>
                    <IconButton
                      onClick={handlePrint}
                      disabled={
                        status === "loading" ||
                        status === "error" ||
                        isReprinting ||
                        statusKey !== "ready"
                      }
                      aria-label={t(
                        "pages.dsokDetails.accessSystem.detail.actions.print.tooltip",
                        { defaultValue: "Tlač" },
                      )}
                    >
                      <PrintIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}

              {canSendEmail ? (
                <Tooltip
                  title={t(
                    "pages.dsokDetails.accessSystem.detail.actions.email.tooltip",
                    { defaultValue: "Email" },
                  )}
                >
                  <span>
                    <IconButton
                      onClick={handleComingSoon}
                      disabled={status === "loading" || status === "error"}
                      aria-label={t(
                        "pages.dsokDetails.accessSystem.detail.actions.email.tooltip",
                        { defaultValue: "Email" },
                      )}
                    >
                      <EmailIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}

              {canSendSms ? (
                <Tooltip
                  title={t(
                    "pages.dsokDetails.accessSystem.detail.actions.sms.tooltip",
                    { defaultValue: "SMS" },
                  )}
                >
                  <span>
                    <IconButton
                      onClick={handleComingSoon}
                      disabled={status === "loading" || status === "error"}
                      aria-label={t(
                        "pages.dsokDetails.accessSystem.detail.actions.sms.tooltip",
                        { defaultValue: "SMS" },
                      )}
                    >
                      <LocalPhoneIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
            </>
          ) : null}

          <IconButton
            onClick={onClose}
            aria-label={t("pages.dsokDetails.accessSystem.detail.close", {
              defaultValue: "Zatvoriť",
            })}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {status === "loading" ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={18} />
            <Typography variant="body2">
              {t("pages.dsokDetails.accessSystem.detail.loading", {
                defaultValue: "Načítavam...",
              })}
            </Typography>
          </Box>
        ) : null}

        {status === "error" ? (
          <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
            {t("pages.dsokDetails.accessSystem.detail.loadError", {
              defaultValue: "Nepodarilo sa načítať detail lístka.",
            })}
          </Alert>
        ) : null}

        {status !== "loading" && status !== "error" ? (
          <Stack spacing={2}>
            <SectionCard
              title={t(
                "pages.dsokDetails.accessSystem.detail.sections.basicInfo",
                {
                  defaultValue: "Základné info",
                },
              )}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Stack spacing={0.5} minWidth={0}>
                  <Typography variant="body2">
                    <Box component="span" color="text.secondary">
                      {t("pages.dsokDetails.accessSystem.summary.ticketCode")}:
                    </Box>{" "}
                    <Box component="span" sx={{ fontFamily: "monospace" }}>
                      {noDash(ticketCode)}
                    </Box>
                  </Typography>
                  <Typography variant="body2">
                    <Box component="span" color="text.secondary">
                      {t(
                        "pages.dsokDetails.accessSystem.summary.validityRules",
                        {
                          defaultValue: "Platnosť",
                        },
                      )}
                    </Box>{" "}
                    {noDash(validity.primary)}
                  </Typography>
                  {validity.range ? (
                    <Typography variant="body2">
                      <Box component="span" color="text.secondary">
                        {t(
                          "pages.dsokDetails.accessSystem.summary.validityRange",
                          {
                            defaultValue: "Rozsah",
                          },
                        )}
                        :
                      </Box>{" "}
                      {validity.range}
                    </Typography>
                  ) : null}
                  {validity.secondary ? (
                    <Typography variant="body2" color="text.secondary">
                      {validity.secondary}
                    </Typography>
                  ) : null}
                </Stack>

                <Chip
                  size="small"
                  label={statusLabel}
                  color={chipProps.color}
                  variant={
                    chipProps.color === "default" ? "outlined" : "filled"
                  }
                  sx={chipProps.sx}
                />
              </Stack>

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                {noDash(productName)}
              </Typography>
              {productDesc ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {productDesc}
                </Typography>
              ) : null}
              <Typography variant="body2">
                <Box component="span" color="text.secondary">
                  {t("pages.dsokDetails.accessSystem.detail.fields.price", {
                    defaultValue: "Cena",
                  })}
                  :
                </Box>{" "}
                {price != null ? String(price) : "—"} {currency || ""}
              </Typography>
            </SectionCard>

            <SectionCard
              title={t(
                "pages.dsokDetails.accessSystem.detail.sections.events",
                {
                  defaultValue: "História vstupov/výstupov",
                },
              )}
            >
              {events.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {noDataLabel}
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {t("pages.dsokDetails.accessSystem.detail.columns.at", {
                          defaultValue: "Čas",
                        })}
                      </TableCell>
                      <TableCell>
                        {t(
                          "pages.dsokDetails.accessSystem.detail.columns.type",
                          {
                            defaultValue: "Typ",
                          },
                        )}
                      </TableCell>
                      <TableCell>
                        {t(
                          "pages.dsokDetails.accessSystem.detail.columns.success",
                          { defaultValue: "Úspech" },
                        )}
                      </TableCell>
                      <TableCell>
                        {t(
                          "pages.dsokDetails.accessSystem.detail.columns.gate",
                          {
                            defaultValue: "Brána",
                          },
                        )}
                      </TableCell>
                      <TableCell>
                        {t(
                          "pages.dsokDetails.accessSystem.detail.columns.reason",
                          {
                            defaultValue: "Dôvod",
                          },
                        )}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {events.map((ev, idx) => {
                      const at = formatDateTimeSecondsSk(getAtValue(ev), "");
                      const typeKey = normalizeEventType(ev?.type);
                      const typeLabel = t(
                        `pages.dsokDetails.accessSystem.detail.eventType.${typeKey}`,
                        { defaultValue: String(ev?.type || "—") },
                      );
                      const success =
                        typeof ev?.success === "boolean"
                          ? ev.success
                          : !String(typeKey).startsWith("failed");
                      const gate =
                        ev?.gateId ??
                        ev?.turnstileNumber ??
                        ev?.turnstile ??
                        "";
                      return (
                        <TableRow key={`${idx}-${String(typeKey)}`}>
                          <TableCell>{noDash(at)}</TableCell>
                          <TableCell>{noDash(typeLabel)}</TableCell>
                          <TableCell>
                            {success ? t("buttons.yes") : t("buttons.no")}
                          </TableCell>
                          <TableCell>{noDash(gate)}</TableCell>
                          <TableCell>{noDash(ev?.reason)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </SectionCard>

            <SectionCard
              title={t(
                "pages.dsokDetails.accessSystem.detail.sections.statusHistory",
                {
                  defaultValue: "História stavu",
                },
              )}
            >
              {statusHistory.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {noStatusHistoryLabel}
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {t("pages.dsokDetails.accessSystem.detail.columns.at", {
                          defaultValue: "Čas",
                        })}
                      </TableCell>
                      <TableCell>
                        {t(
                          "pages.dsokDetails.accessSystem.detail.columns.from",
                          {
                            defaultValue: "Z",
                          },
                        )}
                      </TableCell>
                      <TableCell>
                        {t("pages.dsokDetails.accessSystem.detail.columns.to", {
                          defaultValue: "Na",
                        })}
                      </TableCell>
                      <TableCell>
                        {t(
                          "pages.dsokDetails.accessSystem.detail.columns.byUserId",
                          {
                            defaultValue: "Používateľ",
                          },
                        )}
                      </TableCell>
                      <TableCell>
                        {t(
                          "pages.dsokDetails.accessSystem.detail.columns.reason",
                          {
                            defaultValue: "Dôvod",
                          },
                        )}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statusHistory.map((h, idx) => (
                      <TableRow
                        key={idx}
                        sx={
                          idx === 0
                            ? {
                                bgcolor: "action.hover",
                                "& .MuiTableCell-root": { fontWeight: 700 },
                              }
                            : undefined
                        }
                      >
                        <TableCell>
                          {noDash(formatDateTimeSecondsSk(getAtValue(h)))}
                        </TableCell>
                        <TableCell>
                          {noDash(
                            getTicketStatusLabel(t, normalizeStatus(h?.from)),
                          )}
                        </TableCell>
                        <TableCell>
                          {noDash(
                            getTicketStatusLabel(t, normalizeStatus(h?.to)),
                          )}
                        </TableCell>
                        <TableCell>{noDash(h?.byUserId)}</TableCell>
                        <TableCell>{noDash(h?.reason)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </SectionCard>

            <SectionCard
              title={t(
                "pages.dsokDetails.accessSystem.detail.sections.reprints",
                {
                  defaultValue: "Dotlače",
                },
              )}
            >
              {reprints.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {noDataLabel}
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {t("pages.dsokDetails.accessSystem.detail.columns.at", {
                          defaultValue: "Čas",
                        })}
                      </TableCell>
                      <TableCell>
                        {t(
                          "pages.dsokDetails.accessSystem.detail.columns.byUserId",
                          {
                            defaultValue: "Používateľ",
                          },
                        )}
                      </TableCell>
                      <TableCell>
                        {t(
                          "pages.dsokDetails.accessSystem.detail.columns.reason",
                          {
                            defaultValue: "Dôvod",
                          },
                        )}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reprints.map((r, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          {noDash(formatDateTimeSecondsSk(getAtValue(r)))}
                        </TableCell>
                        <TableCell>{noDash(r?.byUserId)}</TableCell>
                        <TableCell>{noDash(r?.reason)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </SectionCard>
          </Stack>
        ) : null}

        {status === "error" && loadError ? (
          <Typography variant="caption" color="text.secondary">
            {noDash(loadError?.message)}
          </Typography>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          {t("pages.dsokDetails.accessSystem.detail.close", {
            defaultValue: "Zatvoriť",
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketDetailModal;
